// Pure storefront catalog-rule matcher. Single source of truth for
// the "does this rule apply to this product" decision; imported by
// both the embed bundle (per-page activation) and admin previews.
//
// Two features (Hide Price + Hide Add to Cart) share the exact
// same rule shape — both call shouldHideForProduct() with their
// own HideRules block. The matcher itself is feature-agnostic.
//
// AND-combination semantics: a product matches when (page scope
// allows) AND (vendor check passes) AND (tag check passes). Lets a
// merchant write rules like "Hide price on Hydrogen vendor products
// that ALSO carry the 'wholesale' tag" by setting both filters.
//
// Case-insensitive comparison everywhere — Shopify lets merchants
// edit vendor / tag casing freely (HYDROGEN vs Hydrogen vs hydrogen)
// and the rule should still match.

/** Per-product context the storefront embed extracts from
 *  window.ShopifyAnalytics.meta.product (or per-card markup on
 *  collection / search pages). Admin previews construct this from
 *  a hand-picked product for the "preview your rule" panel. */
export interface ProductMeta {
  vendor: string;
  tags: string[];
  /** True only when the current page is a /products/<handle> page.
   *  Drives the page_scope check below. False for collection /
   *  search / cart / homepage. */
  isProductPage: boolean;
}

/** Rule block — shared shape between Hide Price and Hide ATC.
 *  All fields are required at the type level (the shop-config
 *  public projection forwards them via the public endpoint; the
 *  embed reads them via resolveRules() below which fills in
 *  defaults for any undefined ones). */
export interface HideRules {
  enabled: boolean;
  page_scope: "all" | "product_pages_only";
  vendor_mode: "any" | "include" | "exclude";
  vendors: string[];
  tag_mode: "any" | "include" | "exclude";
  tags: string[];
  /** Combinator when BOTH filters are active:
   *    "any" → vendor match OR tag match → fires
   *    "all" → vendor match AND tag match → fires
   *  Ignored when only one filter is active (nothing to
   *  combine). Defaults to "any" because B2B catalog targeting
   *  is usually additive — see ShopConfig comment for rationale. */
  combinator: "any" | "all";
}

/** Decide whether a rule fires for a specific product on the
 *  current page.
 *
 *  Combinator semantics:
 *    - "any" (default) → vendor match OR tag match → fires
 *    - "all"           → vendor match AND tag match → fires
 *  Only ACTIVE filters (mode !== "any") contribute. When both
 *  modes are "any", the rule is a catch-all and fires for every
 *  in-scope product.
 *
 *  Empty-list semantics:
 *    - include + empty list → no product matches "must be in
 *      this list" → evaluates to false (don't hide for that
 *      half of the combinator)
 *    - exclude + empty list → no product is on the exclusion
 *      list → evaluates to true (eligible to hide)
 *  Both are safe — accidentally saving an empty include list
 *  shouldn't hide every product, and an empty exclude list
 *  shouldn't hide nothing. */
export function shouldHideForProduct(
  rules: HideRules,
  product: ProductMeta,
): boolean {
  if (!rules.enabled) return false;
  if (rules.page_scope === "product_pages_only" && !product.isProductPage) {
    return false;
  }

  const vendorFilterActive = rules.vendor_mode !== "any";
  const tagFilterActive = rules.tag_mode !== "any";

  // No filters configured = catch-all → match every in-scope
  // product. This is the master-toggle-only path (merchant
  // enabled the rule but didn't narrow vendor / tag).
  if (!vendorFilterActive && !tagFilterActive) return true;

  // Evaluate each filter independently. !vendorFilterActive (the
  // "any" case) short-circuits to true so the combinator can
  // ignore that half without separately gating below.
  const vendorOk = !vendorFilterActive || evaluateVendor(rules, product);
  const tagOk = !tagFilterActive || evaluateTag(rules, product);

  // "all" combinator — AND. Both halves must pass; when one
  // filter is inactive its vendorOk/tagOk is `true` so this
  // collapses to "the active filter must pass".
  if (rules.combinator === "all") return vendorOk && tagOk;

  // "any" combinator (default) — OR. Only the ACTIVE halves
  // contribute, so we need explicit branches: if both filters
  // are active we accept either match; otherwise the single
  // active filter's result is the answer.
  if (vendorFilterActive && tagFilterActive) return vendorOk || tagOk;
  if (vendorFilterActive) return vendorOk;
  return tagOk; // tagFilterActive must be true here (we early-exited above when neither was)
}

function evaluateVendor(rules: HideRules, product: ProductMeta): boolean {
  // Only called when vendor_mode !== "any".
  const matched = rules.vendors.some(v => ciEquals(v, product.vendor));
  return rules.vendor_mode === "include" ? matched : !matched;
}

function evaluateTag(rules: HideRules, product: ProductMeta): boolean {
  // Only called when tag_mode !== "any". "match" = product has
  // ANY of the configured rule tags (case-insensitive).
  const matched = rules.tags.some(ruleTag =>
    product.tags.some(pTag => ciEquals(ruleTag, pTag)),
  );
  return rules.tag_mode === "include" ? matched : !matched;
}

/** Case-insensitive string equality with null-safe coercion.
 *  Trims both sides — admin-pasted vendor names sometimes carry
 *  trailing whitespace from copy/paste and shouldn't break
 *  matching. */
function ciEquals(a: string | null | undefined, b: string | null | undefined): boolean {
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
}

/** Resolve a shop_config-shaped pair of fields into a HideRules
 *  block with every field defaulted. The embed and admin previews
 *  both call this so they don't have to repeat the
 *  "undefined → default" coalescing inline. */
export function resolveRules(input: {
  enabled?: boolean;
  page_scope?: "all" | "product_pages_only";
  vendor_mode?: "any" | "include" | "exclude";
  vendors?: string[];
  tag_mode?: "any" | "include" | "exclude";
  tags?: string[];
  combinator?: "any" | "all";
} | null | undefined): HideRules {
  const r = input || {};
  return {
    enabled: r.enabled === true,
    page_scope: r.page_scope === "product_pages_only" ? "product_pages_only" : "all",
    vendor_mode: r.vendor_mode === "include" || r.vendor_mode === "exclude" ? r.vendor_mode : "any",
    vendors: Array.isArray(r.vendors) ? r.vendors : [],
    tag_mode: r.tag_mode === "include" || r.tag_mode === "exclude" ? r.tag_mode : "any",
    tags: Array.isArray(r.tags) ? r.tags : [],
    // combinator defaults to "any" (OR) — see HideRules comment
    // for the rationale.
    combinator: r.combinator === "all" ? "all" : "any",
  };
}
